import axios from 'axios';
import CryptoJS from 'crypto-js';

// 滑块验证码专用通道：后端对 /captcha/** 免签名、免解密，
// 因此这里使用独立 axios 实例，不挂任何签名/加密拦截器，发出去的必须是明文 JSON。
const captchaClient = axios.create({
    baseURL: process.env.VUE_APP_API_BASE_URL || '',
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * AJ-Captcha 坐标加密（与后端 com.anji.captcha.util.AESUtil 一致）
 * 算法 AES/ECB/PKCS5Padding，密钥为后端下发 secretKey 的 UTF-8 字节，密文输出 Base64
 * @param {string} content 待加密内容
 * @param {string} secretKey 后端 /captcha/get 返回的密钥
 * @returns {string} Base64 密文
 */
export function aesEncrypt(content, secretKey) {
    return CryptoJS.AES.encrypt(
        CryptoJS.enc.Utf8.parse(content),
        CryptoJS.enc.Utf8.parse(secretKey),
        {
            mode: CryptoJS.mode.ECB,
            padding: CryptoJS.pad.Pkcs7,
        }
    ).toString();
}

// 客户端 UUID：/captcha/get 与 /captcha/check 必须使用同一个，持久化在本地
function getClientUid() {
    let clientUid = localStorage.getItem('slider');
    if (!clientUid) {
        clientUid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
        localStorage.setItem('slider', clientUid);
    }
    return clientUid;
}

/**
 * 本地生成一次性凭证 captchaVerification（后端 check 响应里该字段恒为 null，必须前端算）
 * 公式：AES(token + '---' + 明文坐标JSON, secretKey)
 * @param {string} token /captcha/get 返回的 token
 * @param {string} plain 明文坐标 JSON，形如 {"x":123,"y":5}
 * @param {string} secretKey /captcha/get 返回的密钥
 * @returns {string} Base64 凭证
 */
export function buildCaptchaVerification(token, plain, secretKey) {
    return aesEncrypt(token + '---' + plain, secretKey);
}

/**
 * 获取滑块底图与拼图块
 * @param {string} captchaType 验证码类型，固定 blockPuzzle（滑动拼图）
 * @returns {Promise<{originalImageBase64: string, jigsawImageBase64: string, token: string, secretKey: string}>}
 */
export async function captchaGet(captchaType = 'blockPuzzle') {
    const response = await captchaClient.post('captcha/get', {
        captchaType,
        clientUid: getClientUid(),
        ts: Date.now(),
    });
    const data = response.data;
    // 后端成功标记为 repCode=0000（ResponseModel）
    if (!data || data.repCode !== '0000') {
        throw new Error((data && data.repMsg) || '验证码加载失败，请刷新重试');
    }
    return data.repData || {};
}

/**
 * 提交滑动结果
 * @param {string} captchaType 验证码类型
 * @param {string} token /captcha/get 返回的 token
 * @param {string} pointJson 已加密的坐标（AES(明文坐标JSON, secretKey)）
 * @returns {Promise<void>}
 */
export async function captchaCheck(captchaType, token, pointJson) {
    const response = await captchaClient.post('captcha/check', {
        captchaType,
        token,
        pointJson,
        clientUid: getClientUid(),
        ts: Date.now(),
    });
    const data = response.data;
    if (!data || data.repCode !== '0000') {
        throw new Error((data && data.repMsg) || '验证失败，请重试');
    }
}
